import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, LocationId, OrderType, ResearchTypeId, Anomaly, AnomalyReceivedResult, ActionCompletionToast, AnomalyData, PropertyType, ResearcherData } from '../game/types';
import { MovementSystem } from './MovementSystem';
import { ResearchSystem } from './ResearchSystem';
import { ResourceSystem } from './ResourceSystem';
import { BreachSystem } from './BreachSystem';
import { getAnomalyDisplayName } from '../utils/AnomalyNameResolver';
import { shuffleArray, generateCodeFromProperties } from '../utils/GameUtils';
import { CELL_LOCATIONS } from '../utils/Constants';

export class OrderSystem {
  private breachSystem: BreachSystem;
  private pendingAcceptAnomalies: Map<string, Anomaly> = new Map(); // researcherId -> anomaly
  private pendingCraftBullets: Map<string, string> = new Map(); // researcherId -> silverBulletId
  private pendingRecruitResearchers: Map<string, ResearcherData> = new Map(); // researcherId -> researcherData
  private pendingCoredumpAnomalies: Map<string, string> = new Map(); // researcherId -> anomalyName

  constructor(
    private gameState: GameStateManager,
    private gameData: GameData,
    private movementSystem: MovementSystem,
    private researchSystem: ResearchSystem,
    private resourceSystem: ResourceSystem
  ) {
    this.breachSystem = new BreachSystem(gameState, gameData);
  }

  getBreachSystem(): BreachSystem {
    return this.breachSystem;
  }

  canIssueOrder(researcher: Researcher, order: OrderType, target?: LocationId | string): boolean {
    // CRITICAL: Cannot issue ANY new orders while containing (except cancelling ongoing actions)
    // Containment must not be interrupted
    if (researcher.currentAction === 'Contain') {
      return false;
    }

    switch (order) {
      case 'Move':
        return target !== undefined && this.movementSystem.canMove(researcher, target as LocationId);
      case 'Research':
        return target !== undefined && this.researchSystem.canResearch(researcher, target as LocationId);
      case 'Accept':
        return target !== undefined && researcher.location === target && 
               this.gameState.getAnomalyInCell(target as LocationId) === undefined;
      case 'Coredump':
        if (target === undefined || researcher.location !== target) return false;
        // Cannot coredump if cell is the ORIGIN of a breach
        const breachedFromCell = this.gameState.getBreachedFromCell(target as LocationId);
        return !breachedFromCell;
      case 'Craft':
        return researcher.location === 'control_room' && researcher.currentAction === null;
      case 'Recruit':
        return researcher.location === 'control_room' && researcher.currentAction === null &&
               this.gameState.canAddResearcher() && this.gameState.hasAvailableResearchers();
      case 'Synthetize':
        if (researcher.location !== 'control_room' || researcher.currentAction !== null) return false;
        // Check if chemicals are already at maximum capacity
        const state = this.gameState.getState();
        const chemicals = state.chemicals;
        const maxChemicals = state.maxChemicals;
        return chemicals < maxChemicals;
      case 'Rest':
        if (researcher.location !== 'control_room' || researcher.currentAction !== null) return false;
        // Check if sanity is at max - can't rest if at max unless there are insane researchers
        const maxSanity = researcher.data.sanity;
        const isAtMaxSanity = researcher.currentSanity >= maxSanity;
        if (isAtMaxSanity) {
          // Can only rest if there are insane researchers (they cause constant damage)
          return this.hasInsaneResearchers(researcher.id);
        }
        return true;
      case 'Contain':
        return target !== undefined && this.breachSystem.canContain(researcher, target as LocationId);
      default:
        return false;
    }
  }

  issueMoveOrder(researcher: Researcher, targetLocation: LocationId): boolean {
    if (!this.canIssueOrder(researcher, 'Move', targetLocation)) return false;

    // Cancel any ongoing research before moving (release computing)
    if (researcher.currentAction === 'Research') {
      this.cancelAction(researcher);
    }

    this.movementSystem.startMovement(researcher, targetLocation);
    researcher.currentAction = 'Move';
    return true;
  }

  issueResearchOrder(researcher: Researcher, cellId: LocationId, selectedTypes: ResearchTypeId[]): boolean {
    if (!this.canIssueOrder(researcher, 'Research', cellId)) return false;
    
    // Cancel any ongoing research before starting new one (release computing)
    if (researcher.currentAction === 'Research') {
      this.cancelAction(researcher);
    }
    
    return this.researchSystem.startResearch(researcher, cellId, selectedTypes);
  }

  issueAcceptOrder(researcher: Researcher, cellId: LocationId): boolean {
    if (!this.canIssueOrder(researcher, 'Accept', cellId)) return false;

    // Draw an anomaly from the pool
    const originalAnomalyData = this.gameState.drawAnomaly();
    
    // Check if no more anomalies available
    if (originalAnomalyData === null) {
      // All anomalies solved - trigger victory
      this.gameState.setGameEndState('victory', 'solved');
      return false;
    }

    // Shuffle the properties array to randomize the order
    const shuffledProperties = shuffleArray<PropertyType>(originalAnomalyData.properties);

    // Generate a new code based on the shuffled property order
    const scrambledCode = generateCodeFromProperties(shuffledProperties);

    // Create a new AnomalyData with shuffled properties and scrambled code
    const shuffledAnomalyData: AnomalyData = {
      ...originalAnomalyData,
      code: scrambledCode,
      properties: shuffledProperties
    };

    const anomaly = {
      data: shuffledAnomalyData,
      cellId: cellId as LocationId,
      knownProperties: [shuffledProperties[0]] // First property from shuffled array is known
    };

    // Store anomaly to be added on completion, not immediately
    this.pendingAcceptAnomalies.set(researcher.id, anomaly);

    // Set action
    researcher.currentAction = 'Accept';
    const timer = this.gameData.costs.action_timers.accept;
    researcher.actionTimer = timer;
    researcher.actionTotalTime = timer;

    return true;
  }

  issueCoredumpOrder(researcher: Researcher, cellId: LocationId): boolean {
    if (!this.canIssueOrder(researcher, 'Coredump', cellId)) return false;

    const anomaly = this.gameState.getAnomalyInCell(cellId);
    if (!anomaly || anomaly.knownProperties.length < 2) return false;

    // Double-check not from breached cell
    const breachedFromHere = this.gameState.getBreachedFromCell(cellId);
    if (breachedFromHere) return false;

    // Store anomaly name before removing it (for toast notification on completion)
    this.pendingCoredumpAnomalies.set(researcher.id, anomaly.data.name);

    this.gameState.removeAnomaly(anomaly);

    researcher.currentAction = 'Coredump';
    const timer = this.gameData.costs.action_timers.coredump;
    researcher.actionTimer = timer;
    researcher.actionTotalTime = timer;

    return true;
  }

  issueCraftOrder(researcher: Researcher, silverBulletId: string): boolean {
    if (!this.canIssueOrder(researcher, 'Craft')) return false;

    const costs = this.gameData.costs;
    if (!this.gameState.spendBudget(costs.silver_bullet_cost)) return false;

    // Store silver bullet to be assigned on completion, not immediately
    this.pendingCraftBullets.set(researcher.id, silverBulletId);

    researcher.currentAction = 'Craft';
    const timer = costs.action_timers.craft;
    researcher.actionTimer = timer;
    researcher.actionTotalTime = timer;

    return true;
  }

  issueRecruitOrder(researcher: Researcher): boolean {
    if (!this.canIssueOrder(researcher, 'Recruit')) return false;

    // Check if recruitment is forbidden by reality effect
    if (this.gameState.hasActiveEffect('recruitmentForbidden')) {
      return false;
    }

    const costs = this.gameData.costs;
    if (!this.gameState.spendBudget(costs.recruit_cost)) return false;

    // Get next available researcher data from the pool (excluding already recruited ones)
    const newResearcherData = this.gameState.getNextAvailableResearcher();
    if (!newResearcherData) return false; // No available researchers in pool

    // Store researcher data to be added on completion, not immediately
    this.pendingRecruitResearchers.set(researcher.id, newResearcherData);

    researcher.currentAction = 'Recruit';
    researcher.actionTimer = costs.recruit_time;
    researcher.actionTotalTime = costs.recruit_time;

    return true;
  }

  issueSynthetizeOrder(researcher: Researcher): boolean {
    if (!this.canIssueOrder(researcher, 'Synthetize')) return false;

    // Check if chemicals are already at maximum capacity
    const state = this.gameState.getState();
    const chemicals = state.chemicals;
    const maxChemicals = state.maxChemicals;
    if (chemicals >= maxChemicals) {
      return false; // Cannot synthetize when at max capacity
    }

    researcher.currentAction = 'Synthetize';
    researcher.actionTimer = 0; // Ongoing action, start at 0 and increment
    researcher.actionTotalTime = -1;

    return true;
  }

  issueRestOrder(researcher: Researcher): boolean {
    if (!this.canIssueOrder(researcher, 'Rest')) return false;

    researcher.currentAction = 'Rest';
    researcher.actionTimer = 0; // Ongoing action, start at 0 and increment
    researcher.actionTotalTime = -1;

    return true;
  }

  issueContainOrder(researcher: Researcher, cellId: LocationId): boolean {
    if (!this.canIssueOrder(researcher, 'Contain', cellId)) return false;
    return this.breachSystem.startContainment(researcher, cellId);
  }

  private releaseResearchComputing(researcher: Researcher): void {
    // Release computing reserved for research action
    if (researcher.researchTypes) {
      const researchTypes = this.gameData.researchTypes.filter(rt => 
        researcher.researchTypes!.includes(rt.id)
      );
      let totalComputing = 0;
      for (const rt of researchTypes) {
        for (const cost of rt.costs) {
          if (cost.computation) {
            totalComputing += cost.computation;
          }
        }
      }
      if (totalComputing > 0) {
        this.gameState.releaseComputing(totalComputing);
      }
    }
  }

  cancelOngoingAction(researcher: Researcher): void {
    if (researcher.currentAction === 'Synthetize' || researcher.currentAction === 'Rest') {
      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
    } else if (researcher.currentAction === 'Research') {
      // Release computing when cancelling research
      this.releaseResearchComputing(researcher);
      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
      researcher.researchTypes = undefined;
    }
  }

  cancelAction(researcher: Researcher): void {
    // Cancel any action without completion
    // If it's a research action, release computing
    if (researcher.currentAction === 'Research') {
      this.releaseResearchComputing(researcher);
      researcher.researchTypes = undefined;
    }
    researcher.currentAction = null;
    researcher.actionTimer = 0;
    researcher.actionTotalTime = 0;
  }

  completeSynthetize(researcher: Researcher): void {
    if (researcher.currentAction === 'Synthetize') {
      // Generate toast
      const chemicals = this.gameState.getChemicals();
      const maxChemicals = this.gameState.getState().maxChemicals;
      const toast: ActionCompletionToast = {
        researcherName: researcher.data.name,
        location: researcher.location!,
        actionType: 'Synthetize',
        summary: `Chemicals at maximum capacity (${chemicals}/${maxChemicals})`,
        timestamp: Date.now()
      };
      this.gameState.queueToast(toast);

      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
    }
  }

  completeRest(researcher: Researcher): void {
    if (researcher.currentAction === 'Rest') {
      // Generate toast
      const maxSanity = researcher.data.sanity;
      const toast: ActionCompletionToast = {
        researcherName: researcher.data.name,
        location: researcher.location!,
        actionType: 'Rest',
        summary: `Sanity restored to maximum (${maxSanity}/${maxSanity})`,
        timestamp: Date.now()
      };
      this.gameState.queueToast(toast);

      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
    }
  }

  completeAction(researcher: Researcher): void {
    if (researcher.currentAction === 'Research') {
      // Find which cell the researcher is researching
      for (const cellId of CELL_LOCATIONS) {
        const anomaly = this.gameState.getAnomalyInCell(cellId);
        if (anomaly && researcher.location === cellId) {
          this.researchSystem.completeResearch(researcher, cellId);
          return;
        }
      }
    } else if (researcher.currentAction === 'Contain') {
      // Find which cell the researcher is containing
      for (const cellId of CELL_LOCATIONS) {
        const breached = this.gameState.getBreachedInCell(cellId);
        if (breached && researcher.location === cellId) {
          this.breachSystem.completeContainment(researcher, cellId);
          return;
        }
      }
    } else if (researcher.currentAction === 'Accept') {
      // Set pending state for modal, then add anomaly after modal is closed
      const pendingAnomaly = this.pendingAcceptAnomalies.get(researcher.id);
      if (pendingAnomaly) {
        // Get resolved name (1 property known at this point)
        const resolvedName = getAnomalyDisplayName(pendingAnomaly, this.gameData);
        
        // Create result for modal
        const result: AnomalyReceivedResult = {
          anomalyName: resolvedName,
          anomalyCode: pendingAnomaly.data.code,
          cellId: pendingAnomaly.cellId!,
          knownProperty: pendingAnomaly.knownProperties[0]
        };
        
        // Queue pending state - modal will be shown by UIManager
        this.gameState.queueAnomalyReceived(result);
        
        // Add the anomaly immediately (modal will show the report)
        this.gameState.addAnomaly(pendingAnomaly);
        this.pendingAcceptAnomalies.delete(researcher.id);

        // Generate toast
        const strings = this.gameData.uiStrings;
        const cellName = strings.locations?.[pendingAnomaly.cellId!] || pendingAnomaly.cellId;
        const toast: ActionCompletionToast = {
          researcherName: researcher.data.name,
          location: researcher.location!,
          actionType: 'Accept',
          summary: `${resolvedName} received in ${cellName}`,
          timestamp: Date.now()
        };
        this.gameState.queueToast(toast);
      }
      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
    } else if (researcher.currentAction === 'Craft') {
      // Assign the silver bullet that was selected when the action started
      const pendingBulletId = this.pendingCraftBullets.get(researcher.id);
      if (pendingBulletId) {
        researcher.silverBullet = pendingBulletId;
        this.pendingCraftBullets.delete(researcher.id);

        // Look up the silver bullet name
        const silverBullet = this.gameData.silverBullets.find(sb => sb.id === pendingBulletId);
        const bulletName = silverBullet?.name || pendingBulletId;

        // Generate toast
        const toast: ActionCompletionToast = {
          researcherName: researcher.data.name,
          location: researcher.location!,
          actionType: 'Craft',
          summary: `${bulletName} crafted`,
          timestamp: Date.now()
        };
        this.gameState.queueToast(toast);
      } else {
        // Defensive: Generate toast even if pending data is missing (shouldn't happen, but ensure toast always shows)
        console.warn(`Craft action completed but pending bullet ID missing for researcher ${researcher.id}`);
        const toast: ActionCompletionToast = {
          researcherName: researcher.data.name,
          location: researcher.location!,
          actionType: 'Craft',
          summary: `Silver bullet crafted`,
          timestamp: Date.now()
        };
        this.gameState.queueToast(toast);
      }
      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
    } else if (researcher.currentAction === 'Coredump') {
      // Retrieve the anomaly name that was stored when the order was issued
      const anomalyName = this.pendingCoredumpAnomalies.get(researcher.id);
      if (anomalyName) {
        // Generate toast
        const toast: ActionCompletionToast = {
          researcherName: researcher.data.name,
          location: researcher.location!,
          actionType: 'Coredump',
          summary: `${anomalyName} coredumped away`,
          timestamp: Date.now()
        };
        this.gameState.queueToast(toast);
        this.pendingCoredumpAnomalies.delete(researcher.id);
      }
      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
    } else if (researcher.currentAction === 'Recruit') {
      // Retrieve the researcher data that was stored when the order was issued
      const pendingResearcherData = this.pendingRecruitResearchers.get(researcher.id);
      if (pendingResearcherData) {
        // Add the researcher now that the timer has completed
        this.gameState.addResearcher(pendingResearcherData);
        this.pendingRecruitResearchers.delete(researcher.id);

        // Generate toast
        const toast: ActionCompletionToast = {
          researcherName: researcher.data.name,
          location: researcher.location!,
          actionType: 'Recruit',
          summary: `${pendingResearcherData.name} recruited`,
          timestamp: Date.now()
        };
        this.gameState.queueToast(toast);
      } else {
        // Defensive: Generate toast even if pending data is missing (shouldn't happen, but ensure toast always shows)
        console.warn(`Recruit action completed but pending researcher data missing for researcher ${researcher.id}`);
        const toast: ActionCompletionToast = {
          researcherName: researcher.data.name,
          location: researcher.location!,
          actionType: 'Recruit',
          summary: `Researcher recruited`,
          timestamp: Date.now()
        };
        this.gameState.queueToast(toast);
      }
      researcher.currentAction = null;
      researcher.actionTimer = 0;
      researcher.actionTotalTime = 0;
    }
  }

  private hasInsaneResearchers(excludeResearcherId?: string): boolean {
    // Check if there are any researchers with sanity below the insane threshold
    const researchers = this.gameState.getResearchers();
    const insaneThreshold = this.gameData.insanityConfig.insane_threshold;
    for (const r of researchers) {
      if (excludeResearcherId && r.id === excludeResearcherId) {
        continue; // Don't check the researcher who is resting
      }
      const maxSanity = r.data.sanity;
      const relativeSanity = r.currentSanity / maxSanity;
      if (relativeSanity < insaneThreshold) {
        return true; // Found an insane researcher
      }
    }
    return false;
  }
}


